// Detail drawer generik untuk resource jaringan lanjutan di AWS Inventory:
// Internet/NAT/Transit Gateway, Route Table, VPC Peering, VPC Endpoint, ENI, Load Balancer.
// Field skalar -> grid, array objek -> tabel kecil, objek -> sub-grid. Teks UI Inggris.
import React from 'react';
import { X } from 'lucide-react';

const KIND = {
  internet_gateway: 'Internet Gateway', nat_gateway: 'NAT Gateway', route_table: 'Route Table',
  transit_gateway: 'Transit Gateway', vpc_peering: 'VPC Peering', vpc_endpoint: 'VPC Endpoint',
  network_interface: 'Network Interface', load_balancer: 'Load Balancer',
  ssm_instance: 'Systems Manager · Managed Instance', log_group: 'CloudWatch Log Group',
  opensearch: 'OpenSearch Domain', acm_certificate: 'ACM Certificate',
};
const LABEL = {
  id: 'ID', name: 'Name', vpcId: 'VPC', subnetId: 'Subnet', subnetIds: 'Subnets', state: 'State', status: 'Status',
  connectivityType: 'Connectivity', publicIp: 'Public IP', privateIp: 'Private IP', allocationId: 'Allocation ID',
  createTime: 'Created', failureMessage: 'Failure', main: 'Main route table', routeCount: 'Routes',
  defaultRoute: 'Default route (0.0.0.0/0)', exposure: 'Exposure', blackholes: 'Blackhole routes',
  propagatingVgws: 'Propagating VGWs', ownerId: 'Owner account', amazonSideAsn: 'Amazon side ASN', description: 'Description',
  requesterVpc: 'Requester VPC', accepterVpc: 'Accepter VPC', expiration: 'Expires', serviceName: 'Service name',
  service: 'Service', type: 'Type', routeTableIds: 'Route tables', privateDns: 'Private DNS', interfaceType: 'Interface type',
  az: 'AZ', instanceId: 'Instance', attachedTo: 'Attached to', requesterManaged: 'Requester managed',
  requesterId: 'Requester', securityGroups: 'Security groups', scheme: 'Scheme', dnsName: 'DNS name', azs: 'AZs',
  listeners: 'Listeners', targetCount: 'Targets', healthyCount: 'Healthy targets', arn: 'ARN', region: 'Region',
  resourceType: 'Resource type', pingStatus: 'SSM status', lastPing: 'Last ping', agentVersion: 'Agent version',
  isLatestVersion: 'Latest agent', platformType: 'Platform type', platformName: 'OS', platformVersion: 'OS version',
  ipAddress: 'IP address', iamRole: 'IAM role', associationStatus: 'Association status', lastAssociationExecution: 'Last association run',
  patchScanned: 'Patch scanned', patchBaseline: 'Patch baseline', patchGroup: 'Patch group', lastPatchOperation: 'Last patch operation',
  lastPatchTime: 'Last patch run', patchInstalled: 'Patches installed', patchMissing: 'Patches missing', patchFailed: 'Patches failed',
  patchPendingReboot: 'Installed, pending reboot', patchCriticalMissing: 'Critical missing', patchSecurityMissing: 'Security missing',
  rebootOption: 'Reboot option', retentionDays: 'Retention (days)', retention: 'Retention', storedBytes: 'Stored bytes',
  storedGiB: 'Stored (GiB)', logGroupClass: 'Log class', metricFilterCount: 'Metric filters', kmsEncrypted: 'KMS encrypted',
  created: 'Created', dataProtection: 'Data protection',
  engineVersion: 'Engine version', instanceType: 'Instance type', instanceCount: 'Data nodes', storageGiB: 'Total storage (GiB)',
  volumeSizeGiB: 'Volume per node (GiB)', zoneAware: 'Zone awareness', encryptAtRest: 'Encryption at rest', nodeToNode: 'Node-to-node encryption',
  fineGrained: 'Fine-grained access', notAfter: 'Expires', daysLeft: 'Days left', inUse: 'In use', inUseBy: 'Used by', sans: 'SANs',
};
const HIDE = new Set(['tags', 'region', 'storedBytes', 'inUseArns']);
const EXPOSURE = {
  public: 'Public (route to Internet Gateway)', 'private-nat': 'Private, internet via NAT Gateway',
  'private-nat-instance': 'Private, internet via NAT instance / ENI', 'private-other': 'Private, default route to other target',
  private: 'Private, no default route',
};

const ISO = /^\d{4}-\d{2}-\d{2}T/;
function fmt(k, v) {
  if (v == null || v === '') return '—';
  if (k === 'exposure') return EXPOSURE[v] || v;
  if (typeof v === 'boolean') return v ? 'Yes' : 'No';
  if (typeof v === 'string' && ISO.test(v)) return new Date(v).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' });
  if (Array.isArray(v)) return v.length ? v.join(', ') : '—';
  return String(v);
}
const human = (k) => LABEL[k] || k.replace(/([A-Z])/g, ' $1').replace(/^./, (c) => c.toUpperCase());

function Table({ rows }) {
  const cols = Array.from(rows.reduce((s, r) => { Object.keys(r).forEach((k) => s.add(k)); return s; }, new Set()));
  return (
    <div className="overflow-x-auto rounded-xl border border-slate-200">
      <table className="w-full text-xs">
        <thead className="bg-slate-50 text-slate-500">
          <tr>{cols.map((c) => <th key={c} className="text-left font-semibold px-3 py-2 whitespace-nowrap">{human(c)}</th>)}</tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-t border-slate-100">
              {cols.map((c) => {
                const v = r[c];
                const bad = (c === 'state' && v === 'blackhole') || (c === 'health' && v && v !== 'healthy');
                return <td key={c} className={'px-3 py-1.5 whitespace-nowrap ' + (bad ? 'text-red-600 font-semibold' : 'text-slate-700')}>{fmt(c, v)}</td>;
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function NetworkDetail({ kind, item, onClose }) {
  if (!item) return null;
  const entries = Object.entries(item).filter(([k]) => !HIDE.has(k) && k.charAt(0) !== '_');
  const scalars = entries.filter(([, v]) => !(v && typeof v === 'object' && !Array.isArray(v)) && !(Array.isArray(v) && v.length && typeof v[0] === 'object'));
  const objects = entries.filter(([, v]) => v && typeof v === 'object' && !Array.isArray(v));
  const tables = entries.filter(([, v]) => Array.isArray(v) && v.length && typeof v[0] === 'object');
  const tags = Array.isArray(item.tags) ? item.tags : [];
  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-[11px] font-semibold uppercase tracking-wider text-brand-600">{KIND[kind] || kind}</div>
          <h3 className="text-lg font-bold text-slate-800 break-words">{item.name || item.id}</h3>
          {item.name && item.id && item.name !== item.id && <div className="text-xs text-slate-400 font-mono">{item.id}</div>}
        </div>
        <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400"><X style={{ width: 18, height: 18 }} /></button>
      </div>
      <div className="grid grid-cols-2 gap-x-6 gap-y-3">
        {scalars.map(([k, v]) => (
          <div key={k} className="min-w-0">
            <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">{human(k)}</div>
            <div className={'text-sm break-words ' + ((k === 'blackholes' && v > 0) || (k === 'status' && v === 'available' && kind === 'network_interface') ? 'text-red-600 font-semibold' : 'text-slate-700')}>{fmt(k, v)}</div>
          </div>
        ))}
      </div>
      {objects.map(([k, v]) => (
        <div key={k}>
          <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-1.5">{human(k)}</div>
          <div className="grid grid-cols-2 gap-x-6 gap-y-2 rounded-xl border border-slate-200 p-3">
            {Object.entries(v).map(([k2, v2]) => (
              <div key={k2}><div className="text-[10px] text-slate-400">{human(k2)}</div><div className="text-sm text-slate-700 break-words">{fmt(k2, v2)}</div></div>
            ))}
          </div>
        </div>
      ))}
      {tables.map(([k, v]) => (
        <div key={k}>
          <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-1.5">{human(k)} ({v.length})</div>
          <Table rows={v} />
        </div>
      ))}
      {tags.length > 0 && (
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-1.5">Tags</div>
          <div className="flex flex-wrap gap-1.5">
            {tags.map((t) => <span key={t.key} className="px-2 py-0.5 rounded-md bg-slate-100 text-xs text-slate-600">{t.key}: {t.value}</span>)}
          </div>
        </div>
      )}
    </div>
  );
}
